import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Table, Form, Spinner, Badge } from "react-bootstrap";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import API from "../utils/api";

const ClientesAnalytics = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");

  // =============================
  // FETCH DATA
  // =============================
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API.urlAPI + "clientes/analytics");
        setClientes(res.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // =============================
  // HELPERS
  // =============================
  const getColorEstado = (estado) => {
    if (estado === "ACTIVO") return "success";
    if (estado === "EN_RIESGO") return "warning";
    return "danger";
  };

  const getScoreColor = (puntaje) => {
    if (puntaje >= 8) return "green";
    if (puntaje >= 5) return "orange";
    return "red";
  };

  const formatMoney = (value) => {
    return `$${Number(value).toLocaleString("es-AR")}`;
  };

  // =============================
  // FILTROS
  // =============================
  const clientesFiltrados = clientes
    .filter((c) => {
      if (filtroEstado) {

  if (filtroEstado === "VIP") {
    return c.puntaje >= 8;
  }

  if (filtroEstado === "RECUPERABLE") {
    return c.estado_cliente === "EN_RIESGO" && c.frecuencia_total >= 5;
  }

  return c.estado_cliente === filtroEstado;
}

      if (busqueda) {
        const nombre = `${c.Nombre} ${c.Apellido}`.toLowerCase();
        return nombre.includes(busqueda.toLowerCase());
      }

      return true;
    })
    .sort((a, b) => new Date(b.ultima_actividad) - new Date(a.ultima_actividad));

  // =============================
  // ALERTAS
  // =============================
  const clientesDormidos = clientes.filter((c) => c.estado_cliente === "DORMIDO");
  const clientesRiesgo = clientes.filter((c) => c.estado_cliente === "EN_RIESGO");
  const clientesTop = clientes.filter((c) => c.puntaje >= 8);
  const recuperables = clientes.filter(
    (c) => c.estado_cliente === "EN_RIESGO" && c.frecuencia_total >= 5
  );

  const dataGrafico = [
    { name: "Activos", value: clientes.filter(c => c.estado_cliente === "ACTIVO").length },
    { name: "En riesgo", value: clientesRiesgo.length },
    { name: "Dormidos", value: clientesDormidos.length }
  ];

  const COLORS = ["#28a745", "#ffc107", "#dc3545"];

  return (
    <div className="container mt-4">
      <h3 className="mb-4">📊 Clientes Analytics</h3>

      {/* KPI */}
      <div className="row text-center">

  <div className="col-md-3">
    <Card 
      className="p-3 shadow-sm h-100"
      style={{ cursor: "pointer" }}
      onClick={() => setFiltroEstado("VIP")}
    >
      <h6>🔥 VIP</h6>
      <h4>{clientesTop.length}</h4>
      <small className="text-muted">
        Clientes con alto puntaje (8 a 10)
      </small>
    </Card>
  </div>

  <div className="col-md-3">
    <Card 
      className="p-3 shadow-sm h-100"
      style={{ cursor: "pointer" }}
      onClick={() => setFiltroEstado("EN_RIESGO")}
    >
      <h6>⚠️ En riesgo</h6>
      <h4>{clientesRiesgo.length}</h4>
      <small className="text-muted">
        Clientes que dejaron de comprar recientemente
      </small>
    </Card>
  </div>

  <div className="col-md-3">
    <Card 
      className="p-3 shadow-sm h-100"
      style={{ cursor: "pointer" }}
      onClick={() => setFiltroEstado("DORMIDO")}
    >
      <h6>❌ Dormidos</h6>
      <h4>{clientesDormidos.length}</h4>
      <small className="text-muted">
        Clientes sin actividad hace tiempo
      </small>
    </Card>
  </div>

  <div className="col-md-3">
    <Card 
      className="p-3 shadow-sm h-100 border-warning"
      style={{ cursor: "pointer" }}
      onClick={() => setFiltroEstado("RECUPERABLE")}
    >
      <h6>🎯 Recuperables</h6>
      <h4>{recuperables.length}</h4>
      <small className="text-muted">
        Clientes en riesgo que antes compraban seguido
      </small>
    </Card>
  </div>

</div>
<Card className="mt-3 shadow-sm">
  <Card.Body>

    <h5 className="text-center mb-3">
      🧠 ¿Cómo se clasifican los clientes?
    </h5>

    <p className="text-center text-muted">
      La clasificación se basa en los días desde la última compra o préstamo
    </p>

    <div className="row text-center mt-3">

      <div className="col-md-4">
        <div className="p-3 border rounded bg-light h-100">
          <h6>🟢 Activos</h6>
          <p className="mb-1"><b>Última actividad ≤ 30 días</b></p>
          <small className="text-muted">
            Clientes que compraron recientemente
          </small>
        </div>
      </div>

      <div className="col-md-4">
        <div className="p-3 border rounded bg-light h-100">
          <h6>🟡 En riesgo</h6>
          <p className="mb-1"><b>31 a 60 días</b></p>
          <small className="text-muted">
            Clientes que dejaron de comprar hace poco
          </small>
        </div>
      </div>

      <div className="col-md-4">
        <div className="p-3 border rounded bg-light h-100">
          <h6>🔴 Dormidos</h6>
          <p className="mb-1"><b>Más de 60 días</b></p>
          <small className="text-muted">
            Clientes sin actividad hace tiempo
          </small>
        </div>
      </div>

    </div>

    <hr />

    <div className="text-center mt-2">
      <p className="mb-1">
        🎯 <b>Recuperables</b>
      </p>
      <small className="text-muted">
        Clientes en riesgo que antes tenían buena frecuencia (≥ 5 operaciones)
      </small>
    </div>

  </Card.Body>
</Card>

      {/* GRAFICO */}
      <Card className="mt-3 shadow-sm">
        <Card.Body>
          <h5 className="text-center mb-3">📊 Distribución de clientes</h5>

          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={dataGrafico} dataKey="value" outerRadius={100} label>
                  {dataGrafico.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card.Body>
      </Card>

      {/* FILTROS */}
      <Card className="mt-3">
        <Card.Body className="d-flex gap-3">
          <Form.Control
            placeholder="Buscar cliente..."
            onChange={(e) => setBusqueda(e.target.value)}
          />

          <Form.Select onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="">Todos</option>
            <option value="ACTIVO">Activos</option>
            <option value="EN_RIESGO">En riesgo</option>
            <option value="DORMIDO">Dormidos</option>
          </Form.Select>
        </Card.Body>
      </Card>

<Card className="mt-3 shadow-sm">
  <Card.Body>

    <h5 className="text-center mb-3">
      📊 ¿Cómo se calcula el puntaje?
    </h5>

    <p className="text-center text-muted">
      El puntaje combina 3 factores: actividad reciente, frecuencia y dinero generado
    </p>

    <div className="row text-center mt-3">

      <div className="col-md-4">
        <div className="p-3 border rounded bg-light h-100">
          <h6>📅 Recencia</h6>
          <p className="mb-1"><b>Última actividad</b></p>
          <small className="text-muted">
            Menor o igual 30 días = mejor puntaje  
            <br />
            Mayor a 60 días = puntaje bajo
          </small>
        </div>
      </div>

      <div className="col-md-4">
        <div className="p-3 border rounded bg-light h-100">
          <h6>🔁 Frecuencia</h6>
          <p className="mb-1"><b>Cantidad de operaciones</b></p>
          <small className="text-muted">
            ≥10 = alto valor  
            <br />
            &lt;5 = bajo impacto
          </small>
        </div>
      </div>

      <div className="col-md-4">
        <div className="p-3 border rounded bg-light h-100">
          <h6>💰 Monetario</h6>
          <p className="mb-1"><b>Dinero generado</b></p>
          <small className="text-muted">
            ≥100.000 = alto valor  
            <br />
            &lt;50.000 = bajo valor
          </small>
        </div>
      </div>

    </div>

    <hr />

    <div className="text-center">
      <p className="mb-2">
        👉 El puntaje final va de <b>1 a 10</b>
      </p>
    </div>

    <div className="row text-center mt-3">

      <div className="col-md-4">
        <div className="p-2 border rounded bg-light">
          <h6>🟢 8 a 10</h6>
          <small>Cliente ideal</small>
        </div>
      </div>

      <div className="col-md-4">
        <div className="p-2 border rounded bg-light">
          <h6>🟡 5 a 7</h6>
          <small>Buen cliente</small>
        </div>
      </div>

      <div className="col-md-4">
        <div className="p-2 border rounded bg-light">
          <h6>🔴 1 a 4</h6>
          <small>Cliente inactivo</small>
        </div>
      </div>

    </div>

  </Card.Body>
</Card>
      {/* TABLA */}
      <Card className="mt-3 shadow-sm">
        <Card.Body>
          <Table striped hover responsive>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Total</th>
                <th>Frecuencia</th>
                <th>Última</th>
                <th>Días</th>
                <th>Puntaje</th>
                <th>Estado</th>
                <th>Observaciones</th>
              </tr>
            </thead>

            <tbody>
              {clientesFiltrados.map((c) => (
                <tr key={c.IdCliente}>
                  <td>{c.Nombre} {c.Apellido}</td>

                  <td>{formatMoney(c.total_general)}</td>

                  <td>{c.frecuencia_total}</td>

                  <td>
                    {c.ultima_actividad
                      ? new Date(c.ultima_actividad).toLocaleDateString()
                      : "-"}
                  </td>

                  <td>{c.dias_sin_actividad}</td>

                  <td style={{ color: getScoreColor(c.puntaje), fontWeight: "bold" }}>
                    ⭐ {c.puntaje}/10
                  </td>

                  <td>
                    <Badge bg={getColorEstado(c.estado_cliente)}>
                      {c.estado_cliente}
                    </Badge>
                  </td>

                  <td>
                    {c.Observaciones ? (
                      <span title={c.Observaciones}>
                        {c.Observaciones.length > 20
                          ? c.Observaciones.substring(0, 20) + "..."
                          : c.Observaciones}
                      </span>
                    ) : "-"}
                  </td>

                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {loading && <Spinner className="mt-3" />}
    </div>
  );
};

export default ClientesAnalytics;